/* dg-spin-l340.js: copy of dg-spin-v401.js (itself a copy of dg-spin.js) for the 34" DGLUX340 (body "34swoosh", no-RGB
   finish; dgs-34swoosh-mesh.bin / dgs-34swoosh-views.json unchanged, dgs-34swooshl340-<view>.webp = the factory renders at
   full resolution, cropped exactly like the packed ones). dg-spin.js and dg-spin-v401.js are untouched. Changes vs dg-spin.js:
   - window.DG_TEX_L340 (object, view name -> URL), when set, replaces opts.textures: the page JS passes the 49" view
     keys (front34r, rear34a, ...) and this body's views are front, front34r, front34l, sider, rear, rear34l, rear34r.
   - phone maxDpr 2.5; textures mipmapped (LinearMipmapLinearFilter) with anisotropy 8, so the full-res renders stay sharp.
   - lit-screen overlay defaults (SCREEN) = the 34swoosh glass area: x -446.3..452.2, bottom 163.2, top 549.1 mm with
     piecewise-linear edge corrections that follow the front render's panel edges, tuned on rendered frames so that at the
     landed view (az 0) the lit area meets the bezel within 0.8 px at 2x on every side with no spill (max 0.15 px). The
     screen canvas takes the area's aspect (1920 x 826), so painters drawing with (w, h) are not stretched. Depth-tested.
   - from dg-spin-v401.js: per-view blend widths (opts.sigmaByView; all 0.55 here), visibility tested in millimetres, the
     front face coloured from the straight front render, loose-test fallback; plus a last fallback to the textures' bled
     edge colour (the base rim's vertices sit just outside every render's silhouette: they were flat-grey dots).
   - the stand (STAND_NOTE). This body's fitted cameras agree on the panel but not on the small stand: the three front
     renders put the post ~9 mm left and ~14 mm further back than the mesh, the rear renders ~1 mm right / 5 mm forward
     (each group is self-consistent to < 0.5 px). So the post and base are placed per render group (PART_FIX): every
     render's depth map is made with the stand where THAT render shows it, and on screen the stand follows the group that
     dominates the current angle (smooth blend through the sides). The post's cable slot is see-through like the real one
     (fragments the straight front/rear render shows as background are discarded; the slot's inner walls are shaded dark),
     and the strip of the post just under the chin, which the landing camera sees but the front render has behind the chin,
     takes the post's own colour instead of a lighter 3/4 side face.
   Original header follows. */
/* Deco Gear photo-projected turntable (production build of proto/mock/duo/spin.js).
   The fitted 3D shape of the monitor is coloured by projecting the REAL factory renders onto it from the
   exact camera each render was taken from, with per-view visibility (depth maps) and view-dependent blending.
   Requires window.THREE (r128).

   Shopify asset URLs are flat CDN URLs, so every file is passed explicitly:
     DGSpin.mount(canvas, {
       mesh:     '{{ "dgs-49hex-mesh.bin" | asset_url }}',          // packed mesh (.bin; legacy model.json also accepted)
       views:    '{{ "dgs-49hex-views.json" | asset_url }}',        // camera poses
       textures: { front: '{{ "dgs-49hex-front.webp" | asset_url }}', ... },  // keyed by view name (falls back to base + view.img)
       screen:   { screenX: 583, screenY0: 214, screenY1: 555 },    // optional lit-screen overlay rectangle (mm)
       onLanded, preserve, maxDpr, depthRes
     }) -> Promise<{ play(o), setAz(deg), setPower(0..1), paintScreen(fn), screenLoop(fn), stopScreen(), draw(), destroy() }>
   Rendering pauses while the canvas is off-screen (or the tab is hidden) and resumes on return. */
(function () {
  // lit-screen area defaults (display-frame mm; see header). top / bottom: [x mm, dy mm] knots, top raised / bottom lowered by dy
  const SCREEN = { x0: -446.26, x1: 452.23, y0: 163.2, y1: 549.1, ct: 0, cb: 0,
    top: [[-446.3, 3.85], [-400.0, 3.27], [-325.0, 2.26], [-250.0, 1.28], [-175.0, 0.44], [-100.0, -0.18], [-25.0, -0.51], [50.0, -0.53], [125.0, -0.24], [200.0, 0.35], [275.0, 1.18], [350.0, 2.17], [425.0, 3.23], [452.2, 3.6]],
    bottom: [[-446.3, -1.59], [-400.0, -0.97], [-325.0, -0.32], [-250.0, 0.0], [-175.0, 0.11], [-100.0, 0.08], [-25.0, -0.04], [50.0, -0.22], [125.0, -0.45], [200.0, -0.77], [275.0, -1.2], [350.0, -1.84], [425.0, -2.78], [452.2, -3.22]] };
  // per-view blend widths (radians); views not listed use opts.sigma or 0.55 (dg-spin.js)
  const SIGMA = {};
  // STAND_NOTE (header): stand part offsets per render group (mm, display frame); see-through slot part + the renders that carve it
  const PART_FIX = { post: { front: [-9, -2, -14], rear: [1, -2, 5], side: [-4, -1, 0] }, base: { front: [0, 0, -3], rear: [0, 1, 6], side: [-4, 0, -3] } };
  const CARVE_PARTS = { post: { face: [.175, .175, .175] } }, CARVE_VIEWS = ['front', 'rear'];   // face: the post's front colour in the front render (45/255)
  const VS = `
    varying vec3 vW; varying vec3 vN; varying vec2 vUv;
    void main(){ vec4 w = modelMatrix*vec4(position,1.); vW=w.xyz; vN=normalize(mat3(modelMatrix)*normal); vUv=uv;
      gl_Position = projectionMatrix*viewMatrix*w; }`;
  const FS = (n) => `
    #include <packing>
    uniform sampler2D tex[${n}]; uniform sampler2D dep[${n}]; uniform mat4 VP[${n}]; uniform vec3 camP[${n}];
    uniform vec3 eye; uniform float isScreen; uniform float power; uniform sampler2D screenTex; uniform float sig[${n}]; uniform float fpow;
    uniform vec2 nf[${n}]; uniform vec4 dpar; uniform float pri[${n}]; uniform float cvw[${n}]; uniform float carve; uniform vec3 pcol;
    varying vec3 vW; varying vec3 vN; varying vec2 vUv;
    float lz(float d, vec2 q){ return 2.*q.x*q.y / ((q.y+q.x) - (2.*d-1.)*(q.y-q.x)); }
    void main(){
      vec3 N = normalize(vN); vec3 toEye = normalize(eye - vW); if (dot(N,toEye) < 0.) N = -N;
      vec3 eyeDir = normalize(vec3(eye.x, 0., eye.z));
      vec3 acc = vec3(0.); float ws = 0.; vec3 acc2 = vec3(0.); float ws2 = 0.; vec3 acc3 = vec3(0.); float ws3 = 0.; float sv = 1.; float sf = 0.;
      ${Array.from({ length: n }, (_, i) => `
      { vec4 c = VP[${i}]*vec4(vW,1.);
        if (c.w > 0.) { vec3 nd = c.xyz/c.w; vec2 uv = nd.xy*.5+.5;
          if (uv.x>=0.&&uv.x<=1.&&uv.y>=0.&&uv.y<=1.) {
            float d = nd.z*.5+.5; float sd = unpackRGBAToDepth(texture2D(dep[${i}], uv));
            vec3 toC = normalize(camP[${i}]-vW); float facing = max(dot(N,toC), 0.);
            float vis0 = clamp((sd - d + 0.0012)/0.0008, 0., 1.);
            float vb = dpar.x + dpar.y/max(facing, .15);
            float vis = dpar.w > .5 ? clamp((lz(sd, nf[${i}]) - c.w + vb)/dpar.z, 0., 1.) : vis0;
            vec4 t = texture2D(tex[${i}], uv);
            if (carve > .5 && cvw[${i}] > .5) { if (t.a < .35) discard;   // see-through stand slot (STAND_NOTE)
              vis = max(vis, vis0); sv = vis; sf = facing; }
            vec3 cd = normalize(vec3(camP[${i}].x, 0., camP[${i}].z));
            float ang = acos(clamp(dot(cd, eyeDir), -1., 1.));
            float w = vis * t.a * pow(facing, fpow) * exp(-ang*ang/(sig[${i}]*sig[${i}])) * (isScreen > .5 ? pri[${i}] : 1.) + 1e-6*vis*t.a;
            acc += w*t.rgb; ws += w;
            float w2 = vis0 * t.a * pow(facing, fpow) * exp(-ang*ang/(sig[${i}]*sig[${i}])) + 1e-6*vis0*t.a;
            acc2 += w2*t.rgb; ws2 += w2;
            float w3 = vis0 * (.02 + facing) * exp(-ang*ang/(sig[${i}]*sig[${i}]));
            acc3 += w3*t.rgb; ws3 += w3; } } }`).join('')}
      vec3 col;
      if (ws > 1e-5) col = acc/ws;
      else if (ws2 > 1e-5) col = acc2/ws2;   // slivers no render sees under the strict test: fall back to dg-spin.js's loose test
      else if (ws3 > 1e-5) col = acc3/ws3;   // mesh just outside every photo's silhouette (base rim vertices): the textures' bled edge colour
      else { float l = .10 + .18*max(dot(N, normalize(vec3(-.4,.8,.6))),0.); col = vec3(l*.95, l*.97, l); }
      // carved part: a surface seen from inside (through the slot) is the slot's shadowed inner wall
      // the strip of the post's face just under the chin: this landing camera (2.5 deg below) sees it, the straight factory
      // render (3.6 deg above) has the chin there, and the 3/4 renders put the lit side face on it -> the face's own colour
      if (carve > .5 && sv < .5 && sf > .5) col = pcol;
      if (carve > .5 && ((carve > 1.5) == gl_FrontFacing)) col = vec3(.085, .085, .09);
      if (isScreen > 1.5 && power > 0.) {
        vec3 s = texture2D(screenTex, vUv).rgb;
        col = mix(col, s, power);
      }
      gl_FragColor = vec4(col, 1.);
    }`;

  // ---- mesh loading -------------------------------------------------------------------------------------
  // Packed .bin (see proto/mock/duo/pkgtest/pack.js): 'DGM1' | u32 headerLen | JSON header | Float32 pos, [Int8 normals], Uint16/32 idx
  function parseBin(ab) {
    const dv = new DataView(ab);
    if (dv.getUint32(0, false) !== 0x44474d31) throw new Error('DGSpin: not a DGM1 mesh');
    const H = dv.getUint32(4, true);
    const hdr = JSON.parse(new TextDecoder().decode(new Uint8Array(ab, 8, H)));
    const D = 8 + H;
    const parts = hdr.parts.map(p => ({
      name: p.name, screen: !!p.screen,
      pos: new Float32Array(ab, D + p.pos, p.n * 3),
      nrm: p.nrm == null ? null : new Int8Array(ab, D + p.nrm, p.n * 3),
      idx: p.it === 32 ? new Uint32Array(ab, D + p.idx, p.i) : new Uint16Array(ab, D + p.idx, p.i),
    }));
    return { parts, ov: hdr.ov };
  }
  // Legacy model.json ({panel:{v,f}, base:{v,f}, ...}): split the panel's screen faces exactly as spin.js does
  function fromLegacy(mesh) {
    const parts = [];
    for (const [k, p] of Object.entries(mesh)) {
      const V = new Float32Array(p.v), F = p.f;
      if (k !== 'panel') { parts.push({ name: k, pos: V, idx: new Uint32Array(F) }); continue; }
      const sF = [], hF = [];
      for (let i = 0; i < F.length; i += 3) {
        const a = F[i] * 3, b = F[i + 1] * 3, c = F[i + 2] * 3;
        const ux = V[b] - V[a], uy = V[b + 1] - V[a + 1], uz = V[b + 2] - V[a + 2], vx = V[c] - V[a], vy = V[c + 1] - V[a + 1], vz = V[c + 2] - V[a + 2];
        let nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx; const l = Math.hypot(nx, ny, nz) || 1; nz /= l;
        const cz = (V[a + 2] + V[b + 2] + V[c + 2]) / 3;
        (nz > .55 && cz > -60 ? sF : hF).push(F[i], F[i + 1], F[i + 2]);
      }
      parts.push({ name: 'hull', pos: V, idx: new Uint32Array(hF) }, { name: 'screen', screen: true, pos: V, idx: new Uint32Array(sF) });
    }
    // overlay depth profile from the panel's vertices (spin.js)
    const pv = mesh.panel.v, bins = {}, BW = 8;
    for (let i = 0; i < pv.length; i += 3) { const x = pv[i], y = pv[i + 1], z = pv[i + 2]; if (y < 260 || y > 500) continue; const b = Math.round(x / BW); if (bins[b] === undefined || z > bins[b]) bins[b] = z; }
    const keys = Object.keys(bins).map(Number).sort((a, b) => a - b), k0 = keys[0];
    return { parts, ov: { bw: BW, k0, z: Array.from({ length: keys[keys.length - 1] - k0 + 1 }, (_, i) => bins[k0 + i] ?? null) } };
  }
  async function loadMesh(url) {
    const r = await fetch(url); if (!r.ok) throw new Error('DGSpin: mesh ' + r.status);
    if (/\.json(\?|$)/i.test(url)) return fromLegacy(await r.json());
    return parseBin(await r.arrayBuffer());
  }

  async function mount(canvas, opts) {
    opts = Object.assign({}, opts || {});
    if (window.DG_TEX_L340 && typeof window.DG_TEX_L340 === 'object') opts.textures = Object.assign({}, window.DG_TEX_L340);
    const base = opts.base || '';
    const sc = Object.assign({}, opts.screen || {});
    const isPhone = (matchMedia('(pointer: coarse)').matches && Math.min(screen.width, screen.height) < 600) || /iPhone|Android.+Mobile/.test(navigator.userAgent);
    const maxDpr = opts.maxDpr || (isPhone ? 2.5 : 2);

    const [meta, mesh] = await Promise.all([
      fetch(opts.views || base + 'dgs-34swoosh-views.json').then(r => { if (!r.ok) throw new Error('DGSpin: views ' + r.status); return r.json(); }),
      loadMesh(opts.mesh || base + 'dgs-34swoosh-mesh.bin'),
    ]);
    const views = meta.views, n = views.length;
    const loader = new THREE.TextureLoader();
    loader.setCrossOrigin('anonymous');
    const texs = await Promise.all(views.map(v => new Promise((res, rej) => loader.load((opts.textures && opts.textures[v.name]) || base + v.img,
      t => { t.minFilter = THREE.LinearMipmapLinearFilter; t.generateMipmaps = true; t.anisotropy = 8; res(t); }, undefined, () => rej(new Error('DGSpin: texture ' + v.name))))));

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, preserveDrawingBuffer: !!opts.preserve });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, maxDpr));

    // geometry: screen faces (front-facing glass) carry a UV over their x/y box for the lit-screen texture
    const scene = new THREE.Scene();
    // lit-screen area (mm). Defaults: the 40hex active panel (see header). opts.screen {screenX, screenY0, screenY1}
    // still works (symmetric rectangle, straight edges); screenX0/screenX1/curveTop/curveBottom refine it.
    const custom = sc.screenX != null || sc.screenY0 != null || sc.screenY1 != null;
    const D = SCREEN;
    const SX0 = sc.screenX0 ?? (sc.screenX != null ? -sc.screenX : D.x0), SX1 = sc.screenX1 ?? (sc.screenX != null ? sc.screenX : D.x1);
    const SY0 = sc.screenY0 ?? D.y0, SY1 = sc.screenY1 ?? D.y1, COLS = 96;
    const CT = sc.curveTop ?? (custom ? 0 : D.ct), CB = sc.curveBottom ?? (custom ? 0 : D.cb);
    // piecewise-linear edge corrections [[x mm, dy mm], ...]: top raised / bottom lowered by dy at x (the photo's panel edges)
    const TK = sc.topKnots || (custom ? [] : D.top), BK = sc.bottomKnots || (custom ? [] : D.bottom);
    const kn = (K, x) => { if (!K || !K.length) return 0; if (x <= K[0][0]) return K[0][1];
      for (let i = 1; i < K.length; i++) if (x <= K[i][0]) return K[i - 1][1] + (x - K[i - 1][0]) / (K[i][0] - K[i - 1][0]) * (K[i][1] - K[i - 1][1]);
      return K[K.length - 1][1]; };
    let KH = 0; for (let c = 0; c <= 32; c++) { const x = SX0 + (SX1 - SX0) * c / 32; KH += (kn(TK, x) + kn(BK, x)) / 33; }
    const screenCanvas = document.createElement('canvas'); screenCanvas.width = 1920;
    screenCanvas.height = Math.round(1920 * (SY1 - SY0 + (CT + CB) / 3 + KH) / (SX1 - SX0));
    const screenTex = new THREE.CanvasTexture(screenCanvas);
    // view-blend width (radians) per view: 0.55 as in dg-spin.js for front/sidel/sider, narrower (0.40) for the 3/4
    // renders, whose features (chin logo, hex panel edges, post) do not land on exactly the same mesh spots as the
    // straight views': wide blending showed a faint second chin logo at the landed front view and ghost lines at the back.
    const SIG = Object.assign({}, SIGMA, opts.sigmaByView || {});
    const sigs = views.map(v => SIG[v.name] || opts.sigma || .55);
    // per-view visibility from the depth maps, compared in millimetres along each factory camera's axis (dg-spin.js
    // compares raw window depth with a 0.0012 bias, which at these camera distances lets anything within ~85 mm of a
    // surface pass as visible: the stand post sits 8-80 mm behind the rear shell, so the photos' post was painted onto the
    // shell as a see-through ghost post). bias = depthBias + depthSlope / facing (mm), ramp = depthRamp (mm).
    const nfs = views.map(v => { const a = v.P[10], b = v.P[14]; return new THREE.Vector2(b / (a - 1), b / (a + 1)); });
    const dpar = new THREE.Vector4(opts.depthBias ?? 5, opts.depthSlope ?? 2, opts.depthRamp ?? 3, opts.legacyDepth ? 0 : 1);
    // the monitor's front face (mesh part flagged 'screen': glass, bezel, chin) is coloured from the straight front render
    // wherever that render covers it; the angled renders' chin logo lands ~7 cm off on this mesh and showed as a second,
    // faint logo (and the side renders lightened the glass near 70 deg).
    const FV = opts.screenView || 'front';
    const pris = views.map(v => v.name === FV ? 1 : (opts.screenOthers ?? .001));
    const uniformsBase = () => ({
      tex: { value: texs }, dep: { value: [] }, VP: { value: [] }, camP: { value: views.map(v => new THREE.Vector3().fromArray(v.C)) },
      eye: { value: new THREE.Vector3() }, isScreen: { value: 0 }, power: { value: 0 }, screenTex: { value: screenTex }, sig: { value: sigs }, fpow: { value: opts.facePow || 1.5 }, nf: { value: nfs }, dpar: { value: dpar }, pri: { value: pris }, cvw: { value: views.map(() => 0) }, carve: { value: 0 }, pcol: { value: new THREE.Vector3() },
    });
    const mats = [], geos = [], fixed = [];
    const FIX = opts.partFix || PART_FIX, CARVE = opts.carve || CARVE_PARTS;
    // view group of each factory render: front (|az| < 60), rear (|az| > 120), side
    const grp = a => { a = ((a + 540) % 360) - 180; return Math.abs(a) < 60 ? 'front' : Math.abs(a) > 120 ? 'rear' : 'side'; };
    const vgrp = views.map(v => grp(v.az));
    const place = w => { for (const [me, f] of fixed) me.position.set(0, 0, 0).addScaledVector(V3(f.front), w.front).addScaledVector(V3(f.rear), w.rear).addScaledVector(V3(f.side), w.side); };
    const V3 = a => new THREE.Vector3().fromArray(a || [0, 0, 0]);
    // 1 when the part's triangles wind inward (then gl_FrontFacing is true on its inside), from the part's centroid
    const inward = g => { let c = [0, 0, 0], k = 0, s = 0; for (let i = 0; i < g.pos.length; i += 3, k++) for (let j = 0; j < 3; j++) c[j] += g.pos[i + j]; c = c.map(v => v / k);
      for (let i = 0; i < g.idx.length; i += 3) { const P = [0, 1, 2].map(j => [0, 1, 2].map(q => g.pos[g.idx[i + j] * 3 + q])), u = P[1].map((v, q) => v - P[0][q]), w = P[2].map((v, q) => v - P[0][q]);
        const n = [u[1] * w[2] - u[2] * w[1], u[2] * w[0] - u[0] * w[2], u[0] * w[1] - u[1] * w[0]]; s += Math.sign(n.reduce((a, v, q) => a + v * ((P[0][q] + P[1][q] + P[2][q]) / 3 - c[q]), 0)); }
      return s < 0 ? 1 : 0; };
    for (const g of mesh.parts) {
      const bg = new THREE.BufferGeometry(), nv = g.pos.length / 3;
      bg.setAttribute('position', new THREE.BufferAttribute(g.pos, 3));
      const uv = new Float32Array(nv * 2);
      if (g.screen) {
        let x0 = 1e9, x1 = -1e9, y0 = 1e9, y1 = -1e9;
        for (let i = 0; i < g.idx.length; i++) { const q = g.idx[i] * 3; x0 = Math.min(x0, g.pos[q]); x1 = Math.max(x1, g.pos[q]); y0 = Math.min(y0, g.pos[q + 1]); y1 = Math.max(y1, g.pos[q + 1]); }
        for (let i = 0; i < nv; i++) { uv[i * 2] = (g.pos[i * 3] - x0) / (x1 - x0); uv[i * 2 + 1] = (g.pos[i * 3 + 1] - y0) / (y1 - y0); }
      }
      bg.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
      bg.setIndex(new THREE.BufferAttribute(g.idx, 1));
      if (g.nrm && !opts.computeNormals) bg.setAttribute('normal', new THREE.BufferAttribute(g.nrm, 3, true)); else bg.computeVertexNormals();
      const u = uniformsBase(); u.isScreen.value = g.screen ? 1 : 0; u.carve.value = CARVE[g.name] ? 1 + inward(g) : 0; if (CARVE[g.name]) u.pcol.value.fromArray(CARVE[g.name].face || [.175, .175, .175]);
      const m = new THREE.ShaderMaterial({ uniforms: u, vertexShader: VS, fragmentShader: FS(n), side: THREE.DoubleSide });
      mats.push(m); geos.push(bg);
      const me = new THREE.Mesh(bg, m); scene.add(me); if (FIX[g.name]) fixed.push([me, FIX[g.name]]);
    }

    // clean curved overlay for the active display area (inset inside the bezel and above the chin)
    const ov = mesh.ov, bins = {}; ov.z.forEach((z, i) => { if (z !== null) bins[ov.k0 + i] = z; });
    const BW = ov.bw, keys = Object.keys(bins).map(Number).sort((a, b) => a - b);
    const zAt = x => { const b = x / BW, i0 = Math.floor(b), i1 = i0 + 1; const z0 = bins[i0] ?? bins[keys[0]], z1 = bins[i1] ?? z0; return z0 + (z1 - z0) * (b - i0); };
    const HX = (SX1 - SX0) / 2, XC = (SX1 + SX0) / 2;
    const ovg = new THREE.BufferGeometry(); const P = [], U = [], I = [];
    for (let c = 0; c <= COLS; c++) {
      const x = SX0 + (SX1 - SX0) * c / COLS, z = zAt(x) + 1.2, q = ((x - XC) / HX) ** 2;
      P.push(x, SY0 - CB * q - kn(BK, x), z, x, SY1 + CT * q + kn(TK, x), z); U.push(c / COLS, 0, c / COLS, 1);
    }
    for (let c = 0; c < COLS; c++) { const a = c * 2; I.push(a, a + 2, a + 1, a + 1, a + 2, a + 3); }
    ovg.setAttribute('position', new THREE.Float32BufferAttribute(P, 3)); ovg.setAttribute('uv', new THREE.Float32BufferAttribute(U, 2)); ovg.setIndex(I);
    geos.push(ovg);
    // depth-tested (dg-spin.js draws it on top of everything): when the lit screen is dragged past ~75 deg, the near end
    // of the monitor now hides the part of the picture behind it instead of the picture being painted over that end.
    const ovMat = new THREE.MeshBasicMaterial({ map: screenTex, transparent: true, opacity: 0, side: THREE.FrontSide, depthWrite: false, depthTest: opts.overlayOnTop ? false : true });
    const ovMesh = new THREE.Mesh(ovg, ovMat); ovMesh.renderOrder = 2; ovMesh.visible = false;

    // per-view depth maps from the exact factory cameras
    const VPs = [], deps = [], rts = [];
    const DR = opts.depthRes || 1536;
    const depthMat = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking, side: THREE.DoubleSide });
    views.forEach((v, k) => {
      place({ front: +(vgrp[k] === 'front'), rear: +(vgrp[k] === 'rear'), side: +(vgrp[k] === 'side') });   // the stand where THIS render shows it
      const cam = new THREE.Camera();
      cam.projectionMatrix.fromArray(v.P); cam.projectionMatrixInverse.copy(cam.projectionMatrix).invert();
      cam.matrixWorldInverse.fromArray(v.V); cam.matrixWorld.copy(cam.matrixWorldInverse).invert(); cam.matrixAutoUpdate = false;
      const w = DR, h = Math.round(DR * v.size[1] / v.size[0]);
      const rt = new THREE.WebGLRenderTarget(w, h, { minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter });
      scene.overrideMaterial = depthMat; renderer.setRenderTarget(rt); renderer.setClearColor(0xffffff, 1); renderer.clear(); renderer.render(scene, cam);
      deps.push(rt.texture); rts.push(rt);
      VPs.push(new THREE.Matrix4().fromArray(v.P).multiply(new THREE.Matrix4().fromArray(v.V)));
    });
    scene.overrideMaterial = null; renderer.setRenderTarget(null); renderer.setClearColor(0x000000, 0);
    depthMat.dispose();
    scene.add(ovMesh);
    for (const m of mats) { m.uniforms.dep.value = deps; m.uniforms.VP.value = VPs; }

    // orbit camera (same height and pitch as the factory renders)
    const cam = new THREE.PerspectiveCamera(22, 1, 50, 20000);
    const target = new THREE.Vector3(0, 300, -30);
    let az = 390, el = -2.5;

    // ---- visibility gating: no GPU work while off-screen or in a hidden tab ----
    let onScreen = true, dirty = false, dead = false;
    const visible = () => onScreen && !document.hidden && !dead;
    function size() {
      const r = canvas.getBoundingClientRect(); if (!r.width || !r.height) return;
      renderer.setSize(r.width, r.height, false); cam.aspect = r.width / r.height; cam.updateProjectionMatrix();
      const t = Math.tan(THREE.MathUtils.degToRad(cam.fov / 2));
      cam.userData.dist = Math.max(1420 / (2 * t * cam.aspect), 720 / (2 * t));
    }
    function render() {
      if (!cam.userData.dist) return;
      const a = THREE.MathUtils.degToRad(az), e = THREE.MathUtils.degToRad(el), d = cam.userData.dist;
      cam.position.set(target.x + d * Math.sin(a) * Math.cos(e), target.y + d * Math.sin(e), target.z + d * Math.cos(a) * Math.cos(e));
      cam.lookAt(target);
      // stand parts follow the photos that dominate this angle (STAND_NOTE); the slot is carved with that group's straight render
      const ca = Math.cos(a), sm = x => { x = Math.min(1, Math.max(0, x / .7)); return x * x * (3 - 2 * x); };
      const w = { front: sm(ca), rear: sm(-ca) }; w.side = 1 - w.front - w.rear; place(w);
      for (const m of mats) { m.uniforms.eye.value.copy(cam.position); if (m.uniforms.carve.value) m.uniforms.cvw.value = views.map((v, k) => CARVE_VIEWS.includes(v.name) ? w[vgrp[k]] || 0 : 0); }
      renderer.render(scene, cam);
    }
    function draw() { if (visible()) { dirty = false; render(); } else dirty = true; }

    let power = 0;
    function setPower(p) { power = p; for (const m of mats) m.uniforms.power.value = p; ovMat.opacity = p; ovMesh.visible = p > 0.001; }

    // screen content loop: paused (not ticking) while off-screen, resumes on return
    let loop = 0, loopFn = null, loopT0 = 0;
    function tick(now) { loop = 0; if (!loopFn || !visible()) return; loopFn(screenCanvas.getContext('2d'), screenCanvas.width, screenCanvas.height, (now - loopT0) / 1000); screenTex.needsUpdate = true; draw(); loop = requestAnimationFrame(tick); }
    function screenLoop(fn) { cancelAnimationFrame(loop); loopFn = fn; loopT0 = performance.now(); tick(loopT0); }
    function stopScreen() { cancelAnimationFrame(loop); loop = 0; loopFn = null; }
    function paintScreen(fn) { fn(screenCanvas.getContext('2d'), screenCanvas.width, screenCanvas.height); screenTex.needsUpdate = true; }

    function resume() { if (!visible()) return; if (loopFn && !loop) loop = requestAnimationFrame(tick); if (dirty) draw(); }
    let io = null, ro = null;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(es => { for (const e of es) onScreen = e.isIntersecting; resume(); }, { rootMargin: '200px 0px' });
      io.observe(canvas);
    }
    const onVis = () => resume();
    document.addEventListener('visibilitychange', onVis);
    const onResize = () => { size(); draw(); };
    if ('ResizeObserver' in window) { ro = new ResizeObserver(onResize); ro.observe(canvas); } else addEventListener('resize', onResize);
    size(); render();

    const ease = t => t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    let raf = 0;
    function play(o) {
      o = o || {}; cancelAnimationFrame(raf); setPower(0);
      const from = o.from ?? 180, to = o.to ?? 0, dur = o.dur ?? 2300, t0 = performance.now();
      return new Promise(res => {
        (function step(now) {
          const t = Math.min(1, (now - t0) / dur); az = from + (to - from) * ease(t); draw();
          if (t < 1) raf = requestAnimationFrame(step);
          else {
            const p0 = performance.now();
            (function on(now2) { const q = Math.min(1, (now2 - p0) / 700); setPower(q * q * (3 - 2 * q)); draw(); if (q < 1) raf = requestAnimationFrame(on); else { res(); opts.onLanded && opts.onLanded(); } })(p0);
          }
        })(t0);
      });
    }
    // drag to turn
    let drag = null;
    const pd = e => { drag = { x: e.clientX, az }; canvas.setPointerCapture(e.pointerId); };
    const pm = e => { if (!drag) return; az = drag.az - (e.clientX - drag.x) * .35; draw(); };
    const pu = () => { drag = null; };
    canvas.addEventListener('pointerdown', pd); canvas.addEventListener('pointermove', pm); canvas.addEventListener('pointerup', pu); canvas.addEventListener('pointercancel', pu);

    function destroy() {
      dead = true; cancelAnimationFrame(raf); stopScreen();
      io && io.disconnect(); ro ? ro.disconnect() : removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVis);
      canvas.removeEventListener('pointerdown', pd); canvas.removeEventListener('pointermove', pm); canvas.removeEventListener('pointerup', pu); canvas.removeEventListener('pointercancel', pu);
      for (const g of geos) g.dispose(); for (const m of mats) m.dispose(); ovMat.dispose();
      for (const t of texs) t.dispose(); for (const r of rts) r.dispose(); screenTex.dispose(); renderer.dispose();
    }
    return { play, setAz: d => { az = d; draw(); }, setPower: p => { setPower(p); draw(); }, paintScreen, screenLoop, stopScreen, draw, destroy };
  }
  window.DGSpin = { mount };
})();
